import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Checkbox, Typography } from '@glplus/glpweb-dsl';
import { Panel } from '../components/Panel';
import { HANDBOOK, HANDBOOK_VERSION, type HandbookBlock, type HandbookLang } from '../handbook/content';
import { runnerApi, RunnerApiError, type MyApplication } from '../lib/runnerApiClient';
import { ORANGE, ORANGE_EYEBROW_SX } from '../styles/colors';

const NAVY = '#343F5B';

/**
 * The Runner Handbook (v1.0, approved 2026-09-11) and its acknowledgement.
 *
 * 🔒 One acknowledgement covers the handbook AND the Kit's not-advice line
 * (Rod 2026-09-10: agree once, at the handbook, not on every Kit screen). Both
 * boxes must be ticked; the server records the version and the Kit timestamp.
 * 🔒 The version is compared, not the fact of an acknowledgement: a bumped
 * HANDBOOK_VERSION asks every Runner again, the same way Terms 2.1 does.
 */
export default function Handbook({ application, reload }: { application: MyApplication | null; reload: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const lang: HandbookLang = i18n.language.startsWith('es') ? 'es' : 'en';
  const doc = HANDBOOK[lang];
  const [readIt, setReadIt] = useState(false);
  const [kitIt, setKitIt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const acked = application?.handbookVersionAcked === HANDBOOK_VERSION;
  const stale = Boolean(application?.handbookVersionAcked) && !acked;

  async function acknowledge() {
    setBusy(true);
    setFlash(null);
    try {
      const res = await runnerApi.acknowledgeHandbook({ handbookVersion: HANDBOOK_VERSION, kitDisclaimerAcknowledged: true });
      setFlash({ kind: 'success', msg: res.advanced ? t('handbook.advanced') : t('handbook.acked', { date: new Date(res.acknowledgedAt ?? Date.now()).toLocaleDateString(), version: HANDBOOK_VERSION }) });
      await reload();
    } catch (e) {
      setFlash({ kind: 'error', msg: e instanceof RunnerApiError ? e.message : t('schedule.saveFailed') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box data-testid="page-handbook" data-handbook-version={HANDBOOK_VERSION} sx={{ maxWidth: 820 }}>
      <Typography component={Link} to="/kit" sx={{ color: '#1f7a3d', fontWeight: 600, fontSize: 13, textDecoration: 'none', display: 'inline-block', mb: 1.5 }}>{t('kit.back')}</Typography>
      <Typography sx={{ ...ORANGE_EYEBROW_SX, mb: 0.5 }}>{t('handbook.kicker', { version: HANDBOOK_VERSION }).toUpperCase()}</Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#1f2937', fontSize: { xs: '24px', md: '30px' }, mb: 0.5 }}>{t('handbook.title')}</Typography>
      <Typography sx={{ color: '#4b5563', mb: 2.5, fontSize: '16px' }}>{acked ? t('handbook.acked', { date: '—', version: HANDBOOK_VERSION }).replace(' — ', ' ') : t('handbook.readAll')}</Typography>
      {stale && (
        <Box sx={{ mb: 2.5 }}>
          <Alert severity="warning" data-testid="handbook-reask">{t('handbook.reAsk')}</Alert>
        </Box>
      )}

      <Box sx={{ backgroundColor: '#fff', borderRadius: 2.5, borderTop: `3px solid ${NAVY}`, p: { xs: 2, md: 3.5 }, boxShadow: '0 1px 2px rgba(16,24,40,0.06)' }}>
        {doc.sections.map((s, i) => (
          <Box key={i} component="section" sx={{ mb: 3 }}>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: '19px', color: '#1f2937', mb: 1 }}>{s.title}</Typography>
            {s.blocks.map((b, j) => <Block key={j} block={b} />)}
          </Box>
        ))}
        {doc.footer && <Typography sx={{ color: '#5f6b7a', fontSize: '13px', fontStyle: 'italic' }}>{doc.footer}</Typography>}
      </Box>

      {application && (
        <Box sx={{ mt: 2.5 }} id="acknowledge">
          <Panel title={t('handbook.ackTitle')} accent={acked ? '#2E9E3A' : ORANGE} dataTestId="handbook-ack-panel">
            {flash && (
              <Box sx={{ mb: 2 }}>
                <Alert severity={flash.kind} data-testid={`handbook-flash-${flash.kind}`}>{flash.msg}</Alert>
              </Box>
            )}
            {acked ? (
              <Typography data-testid="handbook-acked" data-version={application.handbookVersionAcked ?? ''} sx={{ color: '#4b5563', fontSize: '14px' }}>
                {t('handbook.acked', { date: application.trainingAckedAt ? new Date(application.trainingAckedAt).toLocaleDateString() : '—', version: application.handbookVersionAcked })}
              </Typography>
            ) : (
              <>
                <Checkbox data-testid="handbook-ack-handbook" ariaLabel={t('handbook.ackHandbook')} label={t('handbook.ackHandbook')} checked={readIt} onChange={(e) => setReadIt(e.target.checked)} />
                <Checkbox data-testid="handbook-ack-kit" ariaLabel={t('handbook.ackKit')} label={t('handbook.ackKit')} checked={kitIt} onChange={(e) => setKitIt(e.target.checked)} />
                <Box sx={{ mt: 1.5 }}>
                  <Button ariaLabel={t('handbook.ackButton')} dataTestId="handbook-ack-submit" buttonText={t('handbook.ackButton')} variant="contained" size="small" disabled={!readIt || !kitIt || busy} onClick={() => void acknowledge()} sx={{ backgroundColor: ORANGE }} />
                </Box>
              </>
            )}
          </Panel>
        </Box>
      )}
    </Box>
  );
}

/** `**bold**` is the only inline mark the handbook uses; nothing else is interpreted. */
function Inline({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return <>{parts.map((p, i) => (p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <Fragment key={i}>{p}</Fragment>))}</>;
}

function Block({ block }: { block: HandbookBlock }) {
  const textSx = { color: '#374151', fontSize: '15px', lineHeight: 1.65 };
  if (block.type === 'p') return <Typography sx={{ ...textSx, mb: 1 }}><Inline text={block.text} /></Typography>;
  return (
    <Box component={block.type} sx={{ pl: 2.5, m: 0, mb: 1, ...textSx }}>
      {block.items.map((li, j) => <li key={j}><Inline text={li} /></li>)}
    </Box>
  );
}
